import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import {
  isPushSupported, currentPermission, registerSW,
  subscribeUser, onPushNav,
} from "@/lib/push";

/**
 * Banner no intrusivo que ofrece activar Web Push notifications.
 * - Solo aparece para navegadores compatibles con permiso "default"
 *   (usuario nunca decidió).
 * - Recuerda dismiss en localStorage (`push_banner_dismissed_v1`).
 * - Nuevos usuarios: al detectar `firstLogin=true` se dispara un prompt
 *   automático suave (sin banner, solo la request nativa).
 * - Registra el service worker y engancha la nav al hacer click en una
 *   notificación con la app abierta.
 */
export default function PushBanner({ user }) {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  // Registrar SW + wiring de clicks en notificaciones (una sola vez).
  useEffect(() => {
    if (!isPushSupported()) return;
    registerSW();
    const off = onPushNav((url) => navigate(url));
    return off;
  }, [navigate]);

  // Detectar si mostrar el banner (existentes) o disparar el prompt (nuevos).
  useEffect(() => {
    if (!user) return;
    if (!isPushSupported()) return;
    const perm = currentPermission();
    if (perm !== "default") return; // ya decidió (granted/denied) → nada
    const dismissed = localStorage.getItem("push_banner_dismissed_v1");
    // Usuarios nuevos → prompt automático (el AuthContext podría setear firstLogin,
    // pero acá lo aproximamos por si es la 1ª vez que vemos al usuario en este
    // dispositivo). Si prefieres, cambia esta detección.
    const firstSeen = !localStorage.getItem(`push_seen_user_${user.user_id}`);
    if (firstSeen) {
      localStorage.setItem(`push_seen_user_${user.user_id}`, "1");
      // Auto-request suave luego de 3s para no bloquear el primer render.
      const t = setTimeout(async () => {
        const r = await subscribeUser();
        if (r.ok) toast.success("Notificaciones activadas");
        else if (r.reason === "denied") {
          // Marca como dismiss para no molestar más
          localStorage.setItem("push_banner_dismissed_v1", "denied");
        }
      }, 3000);
      return () => clearTimeout(t);
    }
    // Usuarios existentes → banner si no lo cerraron antes
    if (!dismissed) setVisible(true);
  }, [user]);

  if (!visible) return null;

  const activate = async () => {
    setBusy(true);
    const r = await subscribeUser();
    setBusy(false);
    if (r.ok) {
      toast.success("Notificaciones activadas ✓");
      setVisible(false);
      localStorage.setItem("push_banner_dismissed_v1", "granted");
    } else if (r.reason === "denied") {
      toast.error("Permiso denegado. Puedes activarlo desde Mi Ficha.");
      setVisible(false);
      localStorage.setItem("push_banner_dismissed_v1", "denied");
    } else if (r.reason === "notSupported") {
      toast.error("Tu navegador no soporta notificaciones push.");
      setVisible(false);
      localStorage.setItem("push_banner_dismissed_v1", "unsupported");
    } else {
      toast.error("No se pudo activar. Intenta desde Mi Ficha.");
    }
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem("push_banner_dismissed_v1", "user");
  };

  return (
    <div className="w-full bg-cyan-950/60 border-b border-cyan-800/60 text-white" data-testid="push-banner">
      <div className="max-w-6xl mx-auto flex items-center gap-3 px-4 py-2">
        <Bell size={16} className="text-cyan-400 flex-shrink-0"/>
        <div className="flex-1 min-w-0 text-xs sm:text-sm">
          <span className="text-white font-semibold">Recibe avisos del club:</span>
          <span className="text-[#A1A1AA] ml-1 hidden sm:inline">nuevos partidos, cambios en el fixture y publicaciones en Somos Ferro.</span>
        </div>
        <button onClick={activate} disabled={busy} className="text-[10px] sm:text-xs uppercase tracking-widest px-2 sm:px-3 py-1 border border-cyan-400 text-cyan-400 hover:bg-cyan-400/10 disabled:opacity-40 whitespace-nowrap" data-testid="push-banner-activate">
          {busy ? "Activando…" : "Activar"}
        </button>
        <button onClick={dismiss} className="text-[#71717A] hover:text-white flex-shrink-0" title="Cerrar" data-testid="push-banner-dismiss">
          <X size={14}/>
        </button>
      </div>
    </div>
  );
}
